import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { combineLatest, debounceTime, map, Observable, Subject, takeUntil } from 'rxjs';
import { ScrollEndDirective } from 'src/directives/scroll-end.directive';
import { BreakpointsProvider } from 'src/providers/breakpoints.provider';

@Component({
    selector: 'app-split-page',
    templateUrl: './split-page.component.html',
    styleUrls: ['./split-page.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,

})
export class SplitPageComponent implements AfterViewInit {
  private init$ = new Subject<void>();

  private stop$ = new Subject<void>();

  // A template reference is required for the template outlet to work.
  //
  @ContentChild('left') leftTemplate: TemplateRef<any>;

  @ContentChild('right') rightTemplate: TemplateRef<any>;

  // A template reference is required for the template outlet to work.
  //
  @Input() leftLabel: string;

  @Input() rightLabel: string;

  @Input() hide: boolean;

  @Input() whatToHide: 'tab' | 'side' | 'all' = 'all';

  @Input() sideToHide: 'left' | 'right' = 'left';

  @Input() activeTab = 0;

  @Input() leftWidth = 50;

  @Input() scrollEventsLeft = false;

  @Input() scrollEventsRight = false;

  @Input() parentSelectorToListenForLeftScroll?: string;

  @Input() parentSelectorToListenForRightScroll?: string;

  @Input() independentScroll = false;

  @HostBinding('class.independent-scroll')
  get independentScrollClass(): boolean {
    return this.independentScroll;
  }

  @Output()
    selectedTabChange: EventEmitter<MatTabChangeEvent> = new EventEmitter<MatTabChangeEvent>();

  @Output()
    scrollEndLeft: EventEmitter<ElementRef> = new EventEmitter<ElementRef>();

  @Output()
    scrollEndRight: EventEmitter<ElementRef> = new EventEmitter<ElementRef>();

  @Output() scrollTopRight: EventEmitter<number> = new EventEmitter<number>();

  @Output() scroll: EventEmitter<ElementRef> = new EventEmitter<ElementRef>();

  @ViewChild(ScrollEndDirective) scrollEnd: ScrollEndDirective;

  @ViewChild('rightTab') rightPanel: ElementRef;

  @ViewChild('leftTab') leftPanel: ElementRef;

  private get hideSide(): boolean {
    return (
      this.hide && (this.whatToHide === 'side' || this.whatToHide === 'all')
    );
  }

  private get hideTab(): boolean {
    return (
      this.hide && (this.whatToHide === 'tab' || this.whatToHide === 'all')
    );
  }

  private get hideRight(): boolean {
    return this.sideToHide === 'right';
  }

  private get hideLeft(): boolean {
    return this.sideToHide === 'left';
  }

  public get hideLeftSide(): boolean {
    return this.hideSide && this.hideLeft;
  }

  public get hideRightSide(): boolean {
    return this.hideSide && this.hideRight;
  }

  public get hideLeftTab(): boolean {
    return this.hideTab && this.hideLeft;
  }

  public get hideRightTab(): boolean {
    return this.hideTab && this.hideRight;
  }

  public isSmall$: Observable<boolean>;

  public isMobile$: Observable<boolean>;

  public showTabLayout$: Observable<boolean>;

  private rightPanelIsFilled$ = new Subject<boolean>();

  private leftPanelIsFilled$ = new Subject<boolean>();

  private observers: MutationObserver[] = [];

  constructor(private $breakpoint: BreakpointsProvider, private breakpointObserver: BreakpointObserver) {
    this.isSmall$ = this.$breakpoint.isSmall$;
    this.isMobile$ = this.$breakpoint.isMobile$;

    this.showTabLayout$ = combineLatest([
      this.$breakpoint.isSmall$,
      this.breakpointObserver
        // This number should catch the problematically small window sizes for tablets
        // while being below the usual window size for laptops and desktops.
        .observe(['(max-width: 1024px)'])
        .pipe(
          map(result => result.matches),
        ),
    ]).pipe(
      map(([isSmall, isMobile]) => isSmall || isMobile),
    );
  }

  ngOnInit() {
    this.rightPanelIsFilled$
      .pipe(takeUntil(this.stop$), debounceTime(300))
      .subscribe(() => {
        if (!this.rightPanel?.nativeElement) return;
        const isFilled = this.determineIfElementIsFullyFilled(this.rightPanel.nativeElement as Element);
        if (!isFilled && this.scrollEventsRight) this.scrollEndReachedRight(this.rightPanel);
      });

    this.leftPanelIsFilled$
      .pipe(takeUntil(this.stop$), debounceTime(300))
      .subscribe(() => {
        if (!this.leftPanel?.nativeElement) return;
        const isFilled = this.determineIfElementIsFullyFilled(this.leftPanel.nativeElement as Element);
        if (!isFilled && this.scrollEventsLeft) this.scrollEndReachedLeft(this.leftPanel);
      });
  }

  ngAfterViewInit() {
    this.init$.next();

    const rightPanelObserver = new MutationObserver(() => {
      // just signal "something changed"; real work happens after debounce
      this.rightPanelIsFilled$.next(true);
    });

    if (this.rightPanel?.nativeElement && this.scrollEventsRight) {
      rightPanelObserver.observe(this.rightPanel.nativeElement, {
        childList: true,
        subtree: false,
        attributes: false,
      });
    }

    const leftPanelObserver = new MutationObserver(() => {
      this.leftPanelIsFilled$.next(true);
    });

    if (this.leftPanel?.nativeElement && this.scrollEventsLeft) {
      leftPanelObserver.observe(this.leftPanel.nativeElement, {
        childList: true,
        subtree: false,
        attributes: false,
      });
    }

    if (this.observers.length > 0) {
      this.observers.forEach((observer) => observer.disconnect());
    }
    this.observers = [leftPanelObserver, rightPanelObserver];
  }

  ngOnDestroy() {
    this.stop$.next();
    this.observers.forEach((observer) => observer.disconnect());
  }

  private determineIfElementIsFullyFilled(element: Element) {
    const el = element as HTMLElement;
    // true when content overflows (i.e., there's something to scroll)
    return el.scrollHeight > el.clientHeight;
  }

  public selectedTabChangeEvent(event: MatTabChangeEvent) {
    this.selectedTabChange.emit(event);
  }

  scrollEndReachedLeft(reference) {
    this.scrollEndLeft.emit(reference);
  }

  scrollEndReachedRight(reference) {
    this.scrollEndRight.emit(reference);
  }

  scrollTopUpdatedRight(scrollTop) {
    this.scrollTopRight.emit(scrollTop);
  }

  async scrollRight(scrollTop: number) {
    if (this.scrollEnd) {
      this.scrollEnd.scroll(scrollTop);
    }
  }

  onScroll($event: Event) {
    this.scroll.emit($event.target as any);
  }
}
